import React, { useState, useMemo, useCallback, memo, useEffect } from 'react'
import { Theme } from '@material-ui/core'
import {
    DeliverableValueStatus,
    DeliverableValue,
    ResidentTermDirectoryRow,
    EntityClassDataType,
    GroupedValue,
    GroupedValuesByTerm,
} from '../../../../../models'
import { ThreeStateCheckbox } from './ThreeStateCheckbox'
import { StatusIcon } from './StatusIcon'
import {
    Checkbox,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Badge,
    Drawer,
    Container,
    Typography,
} from '@material-ui/core'
import {
    ExpandMore,
    ExpandLess,
    NavigateBefore,
    NavigateNext,
    ArrowUpward,
    ArrowDownward,
    Message,
} from '@material-ui/icons'
import { MessageForm } from '../../../../../components/MessageDrawer/MessageForm'

// Helper function to format values based on data type
const formatValueByDataType = (
    value: string | null,
    dataType: EntityClassDataType,
): string => {
    if (!value) return '—'

    switch (dataType) {
        case 'DATE':
            return new Date(value).toLocaleDateString()
        case 'CURRENCY':
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(Number(value))
        case 'NUMBER':
            return new Intl.NumberFormat('en-US').format(Number(value))
        case 'BOOLEAN':
            return value.toLowerCase() === 'true' ? 'Yes' : 'No'
        default:
            return value
    }
}

interface GroupedDataTableProps {
    theme: Theme
    groupedValues: GroupedValuesByTerm[]
    selectedValueIds: number[]
    onCheckboxToggle: (valueId: number) => void
    onHeaderCheckboxChange: (groupValues: any[]) => void
    getGroupCheckboxState: (
        groupValues: any[],
    ) => {
        checked: boolean
        indeterminate: boolean
        disabled: boolean
    }
    isProcessing: boolean
    onViewDetails?: (
        value: DeliverableValue,
        row: ResidentTermDirectoryRow,
    ) => void
}

// Type for sort fields
type SortField =
    | 'status'
    | 'customerName'
    | 'bldgUnit'
    | 'document_value'
    | 'rent_roll_value'
type SortDirection = 'asc' | 'desc'

// Helper function to format dates consistently
const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

// Helper to get group color
const getGroupHeaderColor = (groupName: string, theme: Theme) => {
    switch (groupName) {
        case 'Current':
            return theme.palette.primary.light
        case 'Future':
            return theme.palette.info.light
        case 'Past':
        default:
            return '#f0f0f0'
    }
}

// Helper to get group description
const getGroupDescription = (
    groupName: string,
    startDate: string,
    endDate: string,
) => {
    const formattedStart = formatDate(startDate)
    const formattedEnd = formatDate(endDate)

    switch (groupName) {
        case 'Current':
            return `Active leases from ${formattedStart} to ${formattedEnd}`
        case 'Future':
            return `Upcoming leases starting ${formattedStart}`
        case 'Past':
            return `Expired leases ending ${formattedEnd}`
        default:
            return `Lease period: ${formattedStart} - ${formattedEnd}`
    }
}

// Get row border color based on group type
const getRowBorderColor = (groupName: string, theme: Theme) => {
    switch (groupName) {
        case 'Current':
            return theme.palette.primary.main
        case 'Future':
            return theme.palette.info.main
        case 'Past':
        default:
            return theme.palette.grey[400]
    }
}

// Pagination control component
const PaginationControls = memo(
    ({
        currentPage,
        totalPages,
        rowsPerPage,
        totalRows,
        onPageChange,
        onRowsPerPageChange,
    }: {
        currentPage: number
        totalPages: number
        rowsPerPage: number
        totalRows: number
        onPageChange: (page: number) => void
        onRowsPerPageChange: (rowsPerPage: number) => void
    }) => {
        const handleRowsPerPageChange = (
            event: React.ChangeEvent<{ value: unknown }>,
        ) => {
            onRowsPerPageChange(event.target.value as number)
            // Reset to first page when changing rows per page
            onPageChange(1)
        }

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 0',
                    borderTop: '1px solid #e0e0e0',
                    marginTop: '8px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ width: 120, marginRight: 16 }}
                    >
                        <InputLabel id="rows-per-page-label">
                            Page size
                        </InputLabel>
                        <Select
                            labelId="rows-per-page-label"
                            value={rowsPerPage}
                            onChange={handleRowsPerPageChange}
                            label="Page size"
                        >
                            <MenuItem value={10}>10 rows</MenuItem>
                            <MenuItem value={25}>25 rows</MenuItem>
                            <MenuItem value={50}>50 rows</MenuItem>
                            <MenuItem value={100}>100 rows</MenuItem>
                        </Select>
                    </FormControl>
                    <div style={{ color: '#666', fontSize: '0.9rem' }}>
                        Showing{' '}
                        {Math.min(
                            (currentPage - 1) * rowsPerPage + 1,
                            totalRows,
                        )}{' '}
                        to {Math.min(currentPage * rowsPerPage, totalRows)} of{' '}
                        {totalRows} entries
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => onPageChange(currentPage - 1)}
                        startIcon={<NavigateBefore />}
                    >
                        Previous
                    </Button>
                    <div style={{ margin: '0 16px', color: '#666' }}>
                        Page {currentPage} of {totalPages}
                    </div>
                    <Button
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => onPageChange(currentPage + 1)}
                        endIcon={<NavigateNext />}
                    >
                        Next
                    </Button>
                </div>
            </div>
        )
    },
)

// Memoized table row component for better performance
const TableRow = memo(
    ({
        item,
        isSelected,
        groupName,
        rowBorderColor,
        theme,
        isProcessing,
        onCheckboxToggle,
        columnWidths,
        onViewDetails,
        setChatDrawerOpen,
    }: {
        item: GroupedValue
        isSelected: boolean
        groupName: string
        rowBorderColor: string
        theme: Theme
        isProcessing: boolean
        onCheckboxToggle: (valueId: number) => void
        columnWidths: { [key: string]: string }
        onViewDetails?: (
            value: DeliverableValue,
            row: ResidentTermDirectoryRow,
        ) => void
        setChatDrawerOpen: (channelId: number, entityTitle: string) => void
    }) => {
        // Create a row object that matches the expected format for onViewDetails
        const handleViewDetails = () => {
            if (onViewDetails) {
                // Create a proper DeliverableValue object from the item value
                const deliverableValue: DeliverableValue = {
                    id: item.value.id,
                    display_value:
                        item.value.document_value ||
                        item.value.rent_roll_value ||
                        '',
                    document_value: item.value.document_value,
                    rent_roll_value: item.value.rent_roll_value,
                    status: item.value.status,
                    entity_classification: {
                        id: 0, // These defaults are necessary since the data from the API might be incomplete
                        name: '',
                        data_type: item.value.data_type || 'TEXT',
                        always_display: false,
                        created_date: new Date().toISOString(),
                        description: '',
                    },
                    channel: item.value.channel ?? 0,
                    message_count: item.value.message_count ?? 0,
                }

                // Create a simplified row object matching the ResidentTermDirectoryRow format
                const rowObject: ResidentTermDirectoryRow = {
                    id: item.rowId,
                    values: [deliverableValue],
                    customer: {
                        id: 0,
                        name: item.customerName,
                        created_date: new Date().toISOString(),
                        email: null,
                        phone: null,
                    },
                    bldg_unit: item.bldgUnit,
                    lease_start: item.leaseStart,
                    lease_end: item.leaseEnd,
                    status: 'INITIAL',
                }

                onViewDetails(deliverableValue, rowObject)
            }
        }

        // Handle message icon click
        const handleChatClick = (e: React.MouseEvent) => {
            e.stopPropagation() // Prevent row click

            // Add chat functionality here based on item.value.channel
            if (item.value.channel) {
                console.log(
                    `Attempting to open chat for channel ${
                        item.value.channel
                    } with ${item.value.message_count ?? 0} messages`,
                )
                setChatDrawerOpen(item.value.channel, item.customerName)
            } else {
                console.error('ERROR: Channel is missing from this item!', {
                    itemId: item.value.id,
                    itemValue: item.value,
                    fullItem: item,
                })
                // Display an alert to indicate the issue
                alert(
                    'Chat is not available for this item - missing channel data. Please check console for details.',
                )
            }
        }

        return (
            <tr
                style={{
                    background: isSelected
                        ? theme.palette.action.selected
                        : groupName === 'Current'
                        ? 'rgba(25, 118, 210, 0.04)'
                        : groupName === 'Future'
                        ? 'rgba(2, 136, 209, 0.04)'
                        : 'transparent',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderLeft: `4px solid ${rowBorderColor}`,
                }}
            >
                <td
                    style={{
                        textAlign: 'center',
                        padding: '12px 0', // Reduced horizontal padding
                        verticalAlign: 'middle',
                    }}
                >
                    <Checkbox
                        checked={isSelected}
                        disabled={isProcessing}
                        onChange={() => onCheckboxToggle(item.value.id)}
                        size="small"
                    />
                </td>
                <td
                    style={{
                        textAlign: 'center',
                        padding: '12px 0', // Remove horizontal padding to save space
                        verticalAlign: 'middle', // Ensure vertical centering
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%', // Ensure the cell takes full height
                    }}
                >
                    <StatusIcon status={item.value.status} theme={theme} />
                </td>
                <td
                    style={{
                        padding: '12px 8px',
                        textAlign: 'left',
                    }}
                >
                    {item.customerName}
                </td>
                <td
                    style={{
                        padding: '12px 8px',
                        textAlign: 'left',
                    }}
                >
                    <div>{item.bldgUnit}</div>
                    <div
                        style={{
                            fontSize: '0.75rem',
                            color: theme.palette.text.secondary,
                            marginTop: '4px',
                        }}
                    >
                        {formatDate(item.leaseStart)} -{' '}
                        {formatDate(item.leaseEnd)}
                    </div>
                </td>
                <td
                    style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                    }}
                >
                    {formatValueByDataType(
                        item.value.document_value,
                        item.value.data_type || 'TEXT',
                    )}
                </td>
                <td
                    style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                    }}
                >
                    {formatValueByDataType(
                        item.value.rent_roll_value,
                        item.value.data_type || 'TEXT',
                    )}
                </td>
                <td
                    style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '8px',
                    }}
                >
                    {/* Details button */}
                    {onViewDetails && (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={isProcessing}
                            onClick={handleViewDetails}
                        >
                            Details
                        </Button>
                    )}

                    {/* Message icon with count */}
                    <IconButton
                        size="small"
                        onClick={handleChatClick}
                        disabled={!item.value.channel}
                        style={{
                            padding: '4px',
                            opacity: item.value.channel ? 1 : 0.5,
                        }}
                    >
                        <Badge
                            badgeContent={item.value.message_count || 0}
                            color="primary"
                        >
                            <Message style={{ color: 'black' }} />
                        </Badge>
                    </IconButton>
                </td>
            </tr>
        )
    },
)

// Memoized accordion group component
const AccordionGroup = memo(
    ({
        group,
        theme,
        selectedValueIds,
        isExpanded,
        setExpandedGroup,
        onCheckboxToggle,
        onHeaderCheckboxChange,
        getGroupCheckboxState,
        isProcessing,
        columnWidths,
        isLastItem,
        onViewDetails,
        setChatDrawerOpen,
    }: {
        group: GroupedValuesByTerm
        theme: Theme
        selectedValueIds: number[]
        isExpanded: boolean
        setExpandedGroup: (termKey: string) => void
        onCheckboxToggle: (valueId: number) => void
        onHeaderCheckboxChange: (groupValues: GroupedValue[]) => void
        getGroupCheckboxState: (
            groupValues: GroupedValue[],
        ) => {
            checked: boolean
            indeterminate: boolean
            disabled: boolean
        }
        isProcessing: boolean
        columnWidths: { [key: string]: string }
        isLastItem: boolean
        onViewDetails?: (
            value: DeliverableValue,
            row: ResidentTermDirectoryRow,
        ) => void
        setChatDrawerOpen: (channelId: number, entityTitle: string) => void
    }) => {
        const { termKey, leaseStart, leaseEnd, values } = group
        const groupName = termKey

        // Pagination state
        const [currentPage, setCurrentPage] = useState(1)
        const [rowsPerPage, setRowsPerPage] = useState(25)

        // Sorting state
        const [sortField, setSortField] = useState<SortField | null>(null)
        const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

        // Reset pagination when accordion expands
        React.useEffect(() => {
            if (isExpanded) {
                setCurrentPage(1)
            }
        }, [isExpanded])

        // Handle column sort
        const handleSort = useCallback(
            (field: SortField) => {
                if (sortField === field) {
                    // Toggle direction if same field
                    setSortDirection((prev) =>
                        prev === 'asc' ? 'desc' : 'asc',
                    )
                } else {
                    // Set new field and default to ascending
                    setSortField(field)
                    setSortDirection('asc')
                }
                // Reset to first page on sort change
                setCurrentPage(1)
            },
            [sortField],
        )

        // Memoize expensive calculations
        const headerColor = useMemo(
            () => getGroupHeaderColor(groupName, theme),
            [groupName, theme],
        )

        const groupDescription = useMemo(
            () => getGroupDescription(groupName, leaseStart, leaseEnd),
            [groupName, leaseStart, leaseEnd],
        )

        const rowBorderColor = useMemo(
            () => getRowBorderColor(groupName, theme),
            [groupName, theme],
        )

        // Get the group checkbox state for the header
        const { checked, indeterminate, disabled } = getGroupCheckboxState(
            values,
        )

        // Pre-compute which values are selected for better performance
        const selectedValuesMap = useMemo(() => {
            const map: { [key: number]: boolean } = {}
            selectedValueIds.forEach((id) => {
                map[id] = true
            })
            return map
        }, [selectedValueIds])

        // Calculate the number of selected items in this group
        const selectedItemsCount = useMemo(() => {
            return values.filter((item) =>
                selectedValueIds.includes(item.value.id),
            ).length
        }, [values, selectedValueIds])

        // Sort the values based on current sort field and direction
        const sortedValues = useMemo(() => {
            if (!sortField) return values

            return [...values].sort((a, b) => {
                let aValue: any
                let bValue: any

                // Extract the appropriate values based on the sort field
                switch (sortField) {
                    case 'status':
                        aValue = a.value.status
                        bValue = b.value.status
                        break
                    case 'customerName':
                        aValue = a.customerName || ''
                        bValue = b.customerName || ''
                        break
                    case 'bldgUnit':
                        aValue = a.bldgUnit || ''
                        bValue = b.bldgUnit || ''
                        break
                    case 'document_value':
                        aValue = a.value.document_value || ''
                        bValue = b.value.document_value || ''
                        break
                    case 'rent_roll_value':
                        aValue = a.value.rent_roll_value || ''
                        bValue = b.value.rent_roll_value || ''
                        break
                    default:
                        return 0
                }

                // Handle string comparisons
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    const result = aValue.localeCompare(bValue)
                    return sortDirection === 'asc' ? result : -result
                }

                // Handle other comparisons
                const result = aValue < bValue ? -1 : aValue > bValue ? 1 : 0
                return sortDirection === 'asc' ? result : -result
            })
        }, [values, sortField, sortDirection])

        // Calculate pagination values
        const totalPages = Math.max(
            1,
            Math.ceil(sortedValues.length / rowsPerPage),
        )

        // Get current page data from sorted values
        const currentPageData = useMemo(() => {
            const startIndex = (currentPage - 1) * rowsPerPage
            return sortedValues.slice(startIndex, startIndex + rowsPerPage)
        }, [sortedValues, currentPage, rowsPerPage])

        // Handle page change
        const handlePageChange = useCallback((page: number) => {
            setCurrentPage(page)
        }, [])

        // Handle rows per page change
        const handleRowsPerPageChange = useCallback((rowsCount: number) => {
            setRowsPerPage(rowsCount)
        }, [])

        // Render sort indicator
        const renderSortIndicator = (field: SortField) => {
            if (sortField !== field) return null

            return sortDirection === 'asc' ? (
                <ArrowUpward
                    style={{
                        fontSize: '0.8rem',
                        marginLeft: '4px',
                        verticalAlign: 'middle',
                    }}
                />
            ) : (
                <ArrowDownward
                    style={{
                        fontSize: '0.8rem',
                        marginLeft: '4px',
                        verticalAlign: 'middle',
                    }}
                />
            )
        }

        return (
            <div
                key={termKey}
                style={{
                    marginBottom: isLastItem ? 0 : theme.spacing(2),
                    borderRadius: '4px',
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                {/* Accordion Header - Always Visible */}
                <div
                    onClick={() => setExpandedGroup(termKey)}
                    style={{
                        backgroundColor: headerColor,
                        padding: theme.spacing(1.5),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'background-color 0.2s',
                        boxShadow: isExpanded
                            ? '0 2px 4px rgba(0,0,0,0.1)'
                            : 'none',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {/* Group selection checkbox */}
                        <div
                            style={{ marginRight: theme.spacing(2) }}
                            onClick={(e: React.MouseEvent) =>
                                e.stopPropagation()
                            }
                        >
                            <ThreeStateCheckbox
                                checked={checked}
                                indeterminate={indeterminate}
                                onChange={() => {
                                    onHeaderCheckboxChange(values)
                                }}
                                disabled={disabled || isProcessing}
                            />
                        </div>

                        {/* Text content - Remove onClick stopPropagation to allow toggling */}
                        <div>
                            <div
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                }}
                            >
                                {groupName} Leases
                                <span
                                    style={{
                                        marginLeft: theme.spacing(1),
                                        fontSize: '0.85rem',
                                        fontWeight: 'normal',
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    •{' '}
                                    {selectedItemsCount > 0
                                        ? `${selectedItemsCount} of ${values.length} selected`
                                        : `${values.length} item${
                                              values.length !== 1 ? 's' : ''
                                          }`}
                                </span>
                            </div>
                            <div
                                style={{
                                    fontSize: '0.75rem',
                                    color: theme.palette.text.secondary,
                                    marginTop: 2,
                                }}
                            >
                                {groupDescription}
                            </div>
                        </div>
                    </div>

                    {/* Expand/collapse icon */}
                    <div>
                        {isExpanded ? (
                            <ExpandLess color="action" />
                        ) : (
                            <ExpandMore color="action" />
                        )}
                    </div>
                </div>

                {/* Accordion Content - Only visible when expanded */}
                {isExpanded && (
                    <div>
                        {/* Table Header */}
                        <div
                            style={{
                                padding: '8px 16px 0',
                                backgroundColor: 'white',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    tableLayout: 'fixed',
                                }}
                            >
                                <colgroup>
                                    <col
                                        style={{ width: columnWidths.checkbox }}
                                    />
                                    <col
                                        style={{ width: columnWidths.status }}
                                    />
                                    <col
                                        style={{ width: columnWidths.resident }}
                                    />
                                    <col style={{ width: columnWidths.unit }} />
                                    <col
                                        style={{
                                            width: columnWidths.documentValue,
                                        }}
                                    />
                                    <col
                                        style={{
                                            width: columnWidths.rentRollValue,
                                        }}
                                    />
                                    <col style={{ width: '100px' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                width: columnWidths.checkbox,
                                                padding: '12px 0',
                                            }}
                                        >
                                            {/* Empty header cell for checkbox column alignment */}
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'center',
                                                padding: '12px 0',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleSort('status')}
                                        >
                                            Status
                                            {renderSortIndicator('status')}
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                padding: '12px 8px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                handleSort('customerName')
                                            }
                                        >
                                            Resident
                                            {renderSortIndicator(
                                                'customerName',
                                            )}
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                padding: '12px 8px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                handleSort('bldgUnit')
                                            }
                                        >
                                            Unit & Lease Term
                                            {renderSortIndicator('bldgUnit')}
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'center',
                                                padding: '12px 8px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                handleSort('document_value')
                                            }
                                        >
                                            Document Value
                                            {renderSortIndicator(
                                                'document_value',
                                            )}
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'center',
                                                padding: '12px 8px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                handleSort('rent_roll_value')
                                            }
                                        >
                                            Rent Roll Value
                                            {renderSortIndicator(
                                                'rent_roll_value',
                                            )}
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'center',
                                                padding: '12px 8px',
                                            }}
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                            </table>
                        </div>

                        {/* Table Body - Only render the current page */}
                        <div
                            style={{
                                padding: '0 16px',
                                backgroundColor: 'white',
                                maxHeight: 'calc(70vh - 350px)',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    tableLayout: 'fixed',
                                }}
                            >
                                <colgroup>
                                    <col
                                        style={{ width: columnWidths.checkbox }}
                                    />
                                    <col
                                        style={{ width: columnWidths.status }}
                                    />
                                    <col
                                        style={{ width: columnWidths.resident }}
                                    />
                                    <col style={{ width: columnWidths.unit }} />
                                    <col
                                        style={{
                                            width: columnWidths.documentValue,
                                        }}
                                    />
                                    <col
                                        style={{
                                            width: columnWidths.rentRollValue,
                                        }}
                                    />
                                    <col style={{ width: '100px' }} />
                                </colgroup>
                                <tbody>
                                    {currentPageData.map((item) => {
                                        const isSelected =
                                            selectedValuesMap[item.value.id] ||
                                            false

                                        return (
                                            <TableRow
                                                key={item.rowId}
                                                item={item}
                                                isSelected={isSelected}
                                                groupName={groupName}
                                                rowBorderColor={rowBorderColor}
                                                theme={theme}
                                                isProcessing={isProcessing}
                                                onCheckboxToggle={
                                                    onCheckboxToggle
                                                }
                                                columnWidths={columnWidths}
                                                onViewDetails={onViewDetails}
                                                setChatDrawerOpen={
                                                    setChatDrawerOpen
                                                }
                                            />
                                        )
                                    })}

                                    {/* Show empty state if no data */}
                                    {currentPageData.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={7}
                                                style={{
                                                    textAlign: 'center',
                                                    padding: '40px 0',
                                                }}
                                            >
                                                No data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        <div style={{ padding: '0 16px 16px' }}>
                            <PaginationControls
                                currentPage={currentPage}
                                totalPages={totalPages}
                                rowsPerPage={rowsPerPage}
                                totalRows={sortedValues.length}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                            />
                        </div>
                    </div>
                )}
            </div>
        )
    },
)

export const GroupedDataTable: React.FC<GroupedDataTableProps> = ({
    theme,
    groupedValues,
    selectedValueIds,
    onCheckboxToggle,
    onHeaderCheckboxChange,
    getGroupCheckboxState,
    isProcessing,
    onViewDetails,
}) => {
    // Debug: Log the entire groupedValues structure
    useEffect(() => {
        // Check specifically for channel and message_count in each group's values
        if (groupedValues && groupedValues.length > 0) {
            groupedValues.forEach((group, groupIndex) => {
                console.log(
                    `Group ${groupIndex} (${group.termKey}) values:`,
                    group.values,
                )

                // Check first few items in each group
                const sampleItems = group.values.slice(0, 3)
                sampleItems.forEach((item, itemIndex) => {
                    console.log(
                        `Sample item ${itemIndex} in group ${group.termKey}:`,
                        {
                            hasChannel: item.value.channel !== undefined,
                            channelValue: item.value.channel,
                            hasMessageCount:
                                item.value.message_count !== undefined,
                            messageCountValue: item.value.message_count,
                        },
                    )
                })
            })
        }
    }, [groupedValues])

    // State to track which group is currently expanded
    const [expandedGroup, setExpandedGroup] = useState<string | null>(
        // Default to 'Current' group if available, otherwise first group or null
        groupedValues.find((group) => group.termKey === 'Current')?.termKey ||
            (groupedValues.length > 0 ? groupedValues[0].termKey : null),
    )

    // Memoize the setExpandedGroup callback to prevent unnecessary re-renders
    const handleSetExpandedGroup = useCallback((termKey: string) => {
        // Toggle behavior: close the section if it's already expanded
        setExpandedGroup((currentExpanded) =>
            currentExpanded === termKey ? null : termKey,
        )
    }, [])

    // Define fixed column widths for consistency across all term groups
    const columnWidthsConfig = useMemo(
        () => ({
            checkbox: '40px', // Reduced from 60px to 40px
            status: '65px', // Further reduced from 80px to 65px
            resident: '200px',
            unit: '150px',
            documentValue: '150px',
            rentRollValue: '150px',
        }),
        [],
    )

    const [chatDrawerOpen, setChatDrawerOpen] = useState(false)
    const [activeChannelId, setActiveChannelId] = useState<number | null>(null)
    const [selectedEntityTitle, setSelectedEntityTitle] = useState<
        string | null
    >(null)

    // Handle opening the chat drawer
    const handleChatDrawerOpen = useCallback(
        (channelId: number, entityTitle: string) => {
            console.log(
                `Opening chat drawer for ${entityTitle} (Channel: ${channelId})`,
            )

            // Set all drawer state in one batch
            setChatDrawerOpen(true)
            setActiveChannelId(channelId)
            setSelectedEntityTitle(entityTitle)
        },
        [],
    )

    if (isProcessing) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '40px 0',
                }}
            >
                <div className="ezos-loader">
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <div style={{ marginLeft: '16px' }}>Processing data...</div>
            </div>
        )
    }

    if (groupedValues.length === 0) {
        return <p>No data available with the selected filters.</p>
    }

    return (
        <div
            style={{
                marginBottom: theme.spacing(2),
                marginTop: theme.spacing(3),
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}
        >
            {groupedValues.map((group, index) => (
                <AccordionGroup
                    key={group.termKey}
                    group={group}
                    theme={theme}
                    selectedValueIds={selectedValueIds}
                    isExpanded={expandedGroup === group.termKey}
                    setExpandedGroup={handleSetExpandedGroup}
                    onCheckboxToggle={onCheckboxToggle}
                    onHeaderCheckboxChange={onHeaderCheckboxChange}
                    getGroupCheckboxState={getGroupCheckboxState}
                    isProcessing={isProcessing}
                    columnWidths={columnWidthsConfig}
                    isLastItem={index === groupedValues.length - 1}
                    onViewDetails={onViewDetails}
                    setChatDrawerOpen={handleChatDrawerOpen}
                />
            ))}

            <Drawer
                variant="temporary"
                anchor="right"
                open={chatDrawerOpen}
                onClose={() => setChatDrawerOpen(false)}
                PaperProps={{
                    style: {
                        boxShadow: theme.shadows[8],
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        width: 400,
                    },
                }}
            >
                <Container
                    style={{
                        padding: theme.spacing(2),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography variant="h6" noWrap>
                        {selectedEntityTitle || 'Messages'}
                    </Typography>
                    <Button
                        onClick={() => setChatDrawerOpen(false)}
                        variant="text"
                    >
                        Close
                    </Button>
                </Container>

                <MessageForm
                    theme={theme}
                    channelId={activeChannelId ?? undefined}
                    secondaryChannelId={null}
                    // afterCreateMessage={handleMessageCreated}
                />
            </Drawer>
        </div>
    )
}
